import { useTranslation } from 'next-i18next';

import { Box, Text } from '@chakra-ui/react';

import { dateHelpers } from '@nocowanie/core';
import { Badge } from '@nocowanie/common-ui';

import { AvantioLabel } from '@app/components/atoms/avantio-label';
import { ForeignPartnerId } from '@app/enums';
import { usePolicyInfo, usePrepaymentLabel } from '@app/hooks';
import { apiTypes } from '@app/models';
import { accommodationSlice, useAppSelector } from '@app/state';

import { ProvisionGuaranteeInfoProps } from './provision-guarantee-info.props';

export const ProvisionGuaranteeInfo = ({
    provision,
    price,
    cancellationRules,
    isWrapped = false,
    displayedDateFormat = 'dd MMM.',
    ...props
}: ProvisionGuaranteeInfoProps) => {
    const { t } = useTranslation(['accommodation', 'common']);

    const currency = useAppSelector(accommodationSlice.selectors.selectCurrency);
    const currentAccommodation = useAppSelector(
        accommodationSlice.selectors.selectCurrentAccommodation,
    );
    const isAvantioAccommodation =
        currentAccommodation?.foreignPartnerId === ForeignPartnerId.avantio;

    const { policyType, policyDate } = usePolicyInfo(cancellationRules);
    const prepaymentLabel = usePrepaymentLabel(cancellationRules, t, currency);

    return (
        <Box {...props}>
            <Badge
                variant={'outline'}
                colorPalette={provision.type === apiTypes.ProvisionType.NoMeal ? 'gray' : 'green'}
                color={provision.type === apiTypes.ProvisionType.NoMeal ? 'gray.500' : 'green.500'}
                size={'md'}
                maxW={isWrapped ? 182 : undefined}
                noWrap={false}
            >
                {provision.type === apiTypes.ProvisionType.NoMeal
                    ? provision.name
                    : `${provision.name} ${t('includedInPrice')}`}
            </Badge>
            <Text as="strong" display={'block'} fontSize={12} color={'gray.600'} mt={2}>
                {isAvantioAccommodation ? (
                    <AvantioLabel
                        labelKey={t('prepaymentRules.fullPrepayment')}
                        tooltipKey={`${t('avantio.prepaymentInfo', { ns: 'common' })}`}
                    />
                ) : (
                    prepaymentLabel
                )}
            </Text>
            <Text as="strong" display={'block'} fontSize={12} color={'gray.600'} mt={2} maxW={270}>
                {isAvantioAccommodation ? (
                    <AvantioLabel
                        labelKey={`${t('avantio.cancellationLabel', { ns: 'common' })}`}
                        tooltipKey={`${t('avantio.cancellationTooltip', { ns: 'common' })}`}
                    />
                ) : (
                    <>
                        {policyType
                            ? t(`cancellationPolicy.${policyType}`)
                            : t('cancellationPolicy.noFee')}{' '}
                        {policyDate
                            ? t('cancellation.toDate', {
                                  value: dateHelpers.format(
                                      new Date(policyDate),
                                      displayedDateFormat,
                                  ),
                              })
                            : null}
                    </>
                )}
            </Text>
        </Box>
    );
};
